import { convertToBoardingServices, convertToDaycareServices } from 'actions/services/ServicesConverter';
import { BoardingServiceDto, DaycareServiceDto } from 'actions/services/ServicesDtos';
import { convertToPetCustomPrice, PetCustomPriceDto } from '@spike/pets-action';
import { PetCustomPrice } from '@spike/pet-model';
import ApiClient from 'api';
import { createTokenConfig } from 'api/ApiClient';
import { getBoardingStandardNightlyPrice } from 'components/CreateBooking/Boarding/utils/boardingPricing';
import { findStandardCustomPrice, resolveStandardPrice } from 'components/CreateBooking/utils/bookingCustomPrices';
import store from 'store';

// Plain requests for the Add/Edit Waitlist form. They are scoped to the location chosen
// in the form (not the logged-in one), so they don't go through the shared Redux slices.

export interface WaitlistFormClientPet {
    id: number;
    name: string;
    image: string | null;
    type: string;
}

export interface WaitlistFormClient {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    pets: Array<WaitlistFormClientPet>;
}

export interface WaitlistFormServiceOption {
    id: number;
    name: string;
    businessArea: string;
    /** Price of one occurrence: the appointment, or one night / day / hour for boarding and daycare. */
    price: number | null;
    /** Minutes, as in the service catalog; null when unknown. */
    durationInMinutes: number | null;
}

export interface WaitlistFormAddonOption {
    id: number;
    name: string;
    businessAreas: Array<string>;
    price: number | null;
}

// Evaluation can't be waitlisted (same rule as the API).
const EXCLUDED_BUSINESS_AREAS = ['evaluation'];

const tokenConfig = () => createTokenConfig(String(store.getState().login.auth.token));

interface ClientDetailDto {
    id: number;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
    pets: Array<{
        id: number;
        name: string;
        pet_type: string;
        image_url: string | null;
        active: boolean;
        deceased: boolean;
    }>;
}

export const fetchWaitlistClient = async (marketplaceId: number, customerId: number): Promise<WaitlistFormClient> => {
    const { data } = await ApiClient.get<ClientDetailDto>(`/customer_details/${customerId}`, {
        ...tokenConfig(),
        params: { marketplace_id: marketplaceId }
    });

    return {
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        phone: data.phone,
        pets: (data.pets ?? [])
            .filter(pet => pet.active && !pet.deceased)
            .map(pet => ({ id: pet.id, name: pet.name, image: pet.image_url || null, type: pet.pet_type }))
    };
};

interface PetServicesDto {
    available_services: Array<{
        service_id: number;
        service_name: string;
        business_area?: string;
        price?: number;
        duration?: number;
    }>;
}

const toPrice = (value?: number | string | null): number | null => {
    const price = Number(value);
    return value === null || value === undefined || value === '' || !Number.isFinite(price) ? null : price;
};

// Pet custom prices override the catalog, as in Add Appointment. Without either, the price is unknown.
const withCustomPrice = (
    catalogPrice: number | null,
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): number | null =>
    catalogPrice === null && !findStandardCustomPrice(customPrices, serviceId)
        ? null
        : resolveStandardPrice(catalogPrice ?? 0, customPrices, serviceId).price;

const fetchPetCustomPrices = async (marketplaceId: number, petId: number): Promise<Array<PetCustomPrice>> => {
    try {
        const { data } = await ApiClient.get<Array<PetCustomPriceDto>>(`/pets/${petId}/custom_prices`, {
            ...tokenConfig(),
            params: { marketplace_id: marketplaceId }
        });
        return data.map(convertToPetCustomPrice);
    } catch {
        return [];
    }
};

// Same catalogs Add Appointment uses for daycare and boarding (unit price and nightly rate).
const fetchPetAreaServices = async <T>(url: string, marketplaceId: number): Promise<Array<T>> => {
    try {
        const { data } = await ApiClient.get<{ services: Array<T> }>(url, {
            ...tokenConfig(),
            params: { marketplace_id: marketplaceId }
        });
        return data?.services ?? [];
    } catch {
        return [];
    }
};

/**
 * Services the pet can get, with the price Add Appointment would use for one occurrence: boarding
 * for 1 night and daycare for 1 day (or 1 hour when it is hourly). The form multiplies it by the
 * requested dates. Transportation has no single price. Memberships, deals and discounts don't apply.
 */
export const fetchWaitlistPetServices = async (
    marketplaceId: number,
    petId: number
): Promise<Array<WaitlistFormServiceOption>> => {
    const { data } = await ApiClient.get<PetServicesDto>(`/pet/${petId}/available_services`, {
        ...tokenConfig(),
        params: { marketplace_id: marketplaceId }
    });

    const available = data.available_services.filter(
        service => !EXCLUDED_BUSINESS_AREAS.includes(service.business_area ?? '')
    );
    const hasArea = (area: string) => available.some(service => service.business_area === area);

    const [customPrices, daycareDtos, boardingDtos] = await Promise.all([
        fetchPetCustomPrices(marketplaceId, petId),
        hasArea('daycare')
            ? fetchPetAreaServices<DaycareServiceDto>(`/service/available_daycares/${petId}`, marketplaceId)
            : Promise.resolve([]),
        hasArea('boarding')
            ? fetchPetAreaServices<BoardingServiceDto>(`/service/available_boardings/${petId}`, marketplaceId)
            : Promise.resolve([])
    ]);

    const daycares = convertToDaycareServices(daycareDtos);
    const boardings = convertToBoardingServices(boardingDtos);

    const getCatalogPrice = (service: PetServicesDto['available_services'][number]): number | null => {
        switch (service.business_area) {
            case 'transportation':
                return null;
            case 'daycare': {
                // The catalog unit is the same for full day, half day and hourly.
                const unitPrice = toPrice(daycares.find(daycare => daycare.serviceId === service.service_id)?.price);
                return unitPrice !== null && unitPrice > 0 ? unitPrice : null;
            }
            case 'boarding': {
                const boarding = boardings.find(current => current.serviceId === service.service_id);
                const nightlyPrice = boarding ? getBoardingStandardNightlyPrice(boarding) : 0;
                return nightlyPrice > 0 ? nightlyPrice : null;
            }
            default:
                return toPrice(service.price);
        }
    };

    return available.map(service => ({
        id: service.service_id,
        name: service.service_name,
        businessArea: service.business_area ?? '',
        durationInMinutes: service.duration ?? null,
        price:
            service.business_area === 'transportation'
                ? null
                : withCustomPrice(getCatalogPrice(service), customPrices, service.service_id)
    }));
};

interface AddonServiceDto {
    id: number;
    name: string;
    price?: number | string | null;
    active: boolean;
    business_areas: Array<string>;
}

export const fetchWaitlistAddons = async (marketplaceId: number): Promise<Array<WaitlistFormAddonOption>> => {
    const { data } = await ApiClient.get<Array<AddonServiceDto>>('/addon_services', {
        ...tokenConfig(),
        params: { marketplace_id: marketplaceId }
    });

    return data
        .filter(addon => addon.active)
        .map(addon => ({
            id: addon.id,
            name: addon.name,
            businessAreas: addon.business_areas ?? [],
            price: toPrice(addon.price)
        }));
};
